import { useEffect, useMemo, useState } from 'react';
import { Camera, CircleDot, Link, LoaderCircle, Radio, RefreshCw, Unplug, Users, Volume2 } from 'lucide-react';
import type { RobotCameraPreviewController } from '@/components/camera/useRobotCameraPreview';
import { RobotBatteryIndicator } from '@/components/robots/RobotBatteryIndicator';
import {
  MAX_ROBOT_CONNECTIONS,
  MAX_ROBOT_VOLUME,
  MIN_ROBOT_VOLUME,
} from '@/connection/MultiRobotConnectionManager';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import type { RobotConnectionState } from '@/connection/RobotConnectionSession';
import { getDirectoryStatusPresentation } from '@/connection/directoryStatus';
import { OFFLINE_MODE } from '@/config/offline';

const STATE_LABELS: Record<RobotConnectionState, string> = {
  idle: '未接続',
  preparing: '準備中',
  connecting: '接続中',
  connected: '接続済み',
  reconnecting: '再接続中',
  offline: 'オフライン',
  failed: '接続失敗',
};

const BUSY_STATES = new Set<RobotConnectionState>(['preparing', 'connecting', 'reconnecting']);
const CAMERA_BUSY_STATES = new Set(['starting', 'switching', 'stopping']);
const USER_CONNECTION_LABELS = {
  idle: '未選択',
  connecting: '接続中',
  connected: '閲覧中',
  disconnected: '切断',
  failed: '接続失敗',
} as const;

type RobotConnectionPanelProps = {
  cameraPreview: RobotCameraPreviewController;
  initialDirectoryView?: 'robots' | 'users';
  currentUserId?: string | null;
};

export function RobotConnectionPanel({
  cameraPreview,
  initialDirectoryView = 'robots',
  currentUserId = null,
}: RobotConnectionPanelProps) {
  const remote = useFiloRemote();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolume] = useState(7);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [volumePending, setVolumePending] = useState(false);
  const [directoryView, setDirectoryView] = useState<'robots' | 'users'>(initialDirectoryView);
  const [directoryDetailsOpen, setDirectoryDetailsOpen] = useState(false);
  const [volumeFeedback, setVolumeFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);
  const sessionCount = Object.keys(remote.snapshot.sessions).length;
  const availableSlots = Math.max(0, MAX_ROBOT_CONNECTIONS - sessionCount);
  const locked = pending || volumePending || remote.snapshot.isConnectionChangeInProgress || remote.snapshot.isRunInProgress;
  const atConnectionLimit = sessionCount + selected.size >= MAX_ROBOT_CONNECTIONS;
  const directoryStatus = getDirectoryStatusPresentation(remote.directoryState);
  const cameraBusy = CAMERA_BUSY_STATES.has(cameraPreview.state.status);

  useEffect(() => {
    setDirectoryView(initialDirectoryView);
  }, [initialDirectoryView]);

  const robots = useMemo(() => [...remote.robots].sort((left, right) => {
    if (left.online !== right.online) return left.online ? -1 : 1;
    return left.displayName.localeCompare(right.displayName, 'ja');
  }), [remote.robots]);
  const users = useMemo(() => [...remote.userFlow.users].sort((left, right) => (
    left.displayName.localeCompare(right.displayName, 'ja')
  )), [remote.userFlow.users]);

  const toggleSelected = (robotId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(robotId)) next.delete(robotId);
      else if (next.size < availableSlots) next.add(robotId);
      return next;
    });
  };

  const runAction = async (action: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setPending(false);
    }
  };

  const connectSelected = () => runAction(async () => {
    const robotIds = [...selected];
    await remote.connectRobots(robotIds);
    setSelected(new Set());
  });

  const applyVolume = async () => {
    if (volumePending) return;
    setVolumePending(true);
    setVolumeFeedback(null);
    try {
      const appliedCount = await remote.changeVolume(volume);
      setVolumeFeedback({ kind: 'success', message: `${appliedCount}台に音量を反映しました。` });
    } catch (caught) {
      setVolumeFeedback({
        kind: 'error',
        message: caught instanceof Error ? caught.message : String(caught),
      });
    } finally {
      setVolumePending(false);
    }
  };

  return (
    <aside id="sidebar-panel-robots" className="robot-panel" aria-label="ロボット接続">
      {directoryView === 'robots' && <div id="robot-directory-content" className="robot-directory-content" aria-label="ロボット一覧">
      <div className="robot-panel__summary">
        <span><CircleDot size={14} /> 接続済み <strong>{remote.connectedCount}</strong>台</span>
        <span>{OFFLINE_MODE ? 'USB / ADB' : 'Directory'} <strong>{OFFLINE_MODE ? (remote.directoryLastError ? '確認が必要' : 'ローカル') : directoryStatus.label}</strong></span>
        <span>{sessionCount}/{MAX_ROBOT_CONNECTIONS}</span>
        <div className="robot-panel__summary-actions">
          <button
            className={`icon-button${volumeOpen ? ' is-selected' : ''}`}
            aria-label={volumeOpen ? '音量設定を閉じる' : '音量設定を開く'}
            title="音量設定"
            aria-expanded={volumeOpen}
            disabled={volumePending}
            onClick={() => setVolumeOpen((open) => !open)}
          >
            <Volume2 size={17} />
          </button>
          <button
            className="icon-button"
            aria-label="ロボット一覧を更新"
            title="一覧を更新"
            disabled={locked || remote.loadingRobots}
            onClick={() => void runAction(remote.refreshRobots)}
          >
            <RefreshCw className={remote.loadingRobots ? 'animate-spin' : ''} size={17} />
          </button>
        </div>
      </div>

      {volumeOpen && (
        <section className="robot-volume" aria-label="音量設定">
          <div className="robot-volume__heading">
            <label htmlFor="robot-volume-range"><Volume2 size={16} /> 音量</label>
            <output htmlFor="robot-volume-range">{volume}</output>
          </div>
          <div className="robot-volume__controls">
            <input
              id="robot-volume-range"
              type="range"
              min={MIN_ROBOT_VOLUME}
              max={MAX_ROBOT_VOLUME}
              step={1}
              value={volume}
              disabled={locked}
              onChange={(event) => {
                setVolume(Number(event.target.value));
                setVolumeFeedback(null);
              }}
            />
            <button
              type="button"
              disabled={locked || remote.connectedCount === 0}
              onClick={() => void applyVolume()}
              aria-label={`音量${volume}を接続中のロボットへ反映`}
            >
              {volumePending && <LoaderCircle className="animate-spin" size={14} />}
              反映
            </button>
          </div>
          {volumeFeedback && (
            <p
              className={`robot-volume__feedback robot-volume__feedback--${volumeFeedback.kind}`}
              role={volumeFeedback.kind === 'error' ? 'alert' : 'status'}
            >
              {volumeFeedback.message}
            </p>
          )}
        </section>
      )}

      {!OFFLINE_MODE && directoryStatus.notice && (
        <p className="robot-panel__notice" role="status">{directoryStatus.notice}</p>
      )}
      {remote.directoryLastError && (
        <div className="robot-panel__error directory-error" role="alert">
          <strong>ロボット一覧を更新できません</strong>
          <span>{OFFLINE_MODE ? 'Pythonサーバー・ADB・USBケーブルを確認してください。' : '現在接続中のロボットはそのまま利用できます。'}</span>
          <div className="directory-error__actions">
            <button type="button" disabled={locked || remote.loadingRobots} onClick={() => void runAction(remote.refreshRobots)}>
              <RefreshCw size={14} />再試行
            </button>
            <button type="button" aria-expanded={directoryDetailsOpen} onClick={() => setDirectoryDetailsOpen((open) => !open)}>
              {directoryDetailsOpen ? '詳細を閉じる' : '詳細'}
            </button>
          </div>
          {directoryDetailsOpen && <code>{remote.directoryLastError}</code>}
        </div>
      )}
      {remote.snapshot.isRunInProgress && (
        <p className="robot-panel__notice">フロー実行中は接続先を固定しています。</p>
      )}
      {remote.snapshot.isConnectionChangeInProgress && !remote.snapshot.isRunInProgress && (
        <p className="robot-panel__notice">接続先を変更しています。完了までお待ちください。</p>
      )}
      {atConnectionLimit && (
        <p className="robot-panel__notice" role="status">
          同時に接続できる上限は{MAX_ROBOT_CONNECTIONS}台です。別のロボットを選ぶ場合は、先に選択を解除してください。
        </p>
      )}
      {(error || remote.snapshot.lastError) && (
        <p className="robot-panel__error" role="alert">{error ?? remote.snapshot.lastError}</p>
      )}

      <div className="robot-list">
        {remote.loadingRobots && robots.length === 0 ? (
          <div className="robot-list__empty"><LoaderCircle className="animate-spin" size={20} /> 一覧を取得しています</div>
        ) : robots.length === 0 ? (
          <div className="robot-list__empty">{OFFLINE_MODE ? 'USB端末がありません。ケーブル接続とUSBデバッグの許可を確認してください。' : '登録済みロボットがありません'}</div>
        ) : robots.map((robot) => {
          const session = remote.snapshot.sessions[robot.robotId];
          const preparationFailure = remote.snapshot.preparationFailures[robot.robotId];
          const isConnected = session?.state === 'connected';
          const isBusy = session ? BUSY_STATES.has(session.state) : false;
          const canSelect = !session && robot.status === 'active' && robot.online && !locked;
          const isSelected = selected.has(robot.robotId);
          const canToggleSelection = canSelect && (isSelected || selected.size < availableSlots);
          const isRepresentative = remote.snapshot.representativeRobotId === robot.robotId;
          const canRepresentCurrentRun = !remote.snapshot.runTargets
            || remote.snapshot.runTargets.generations[robot.robotId] === session?.generation;
          const connectionState = session?.state
            ?? preparationFailure?.state
            ?? (robot.status === 'active' && !robot.online ? 'offline' : 'idle');
          const latestError = session?.lastError ?? preparationFailure?.lastError;
          const availabilityLabel = robot.status === 'pending'
            ? '登録準備中'
            : robot.status === 'disabled'
              ? '無効'
              : robot.online ? '接続可能' : 'オフライン';
          return (
            <article
              className={`robot-row${isConnected ? ' robot-row--connected' : ''}${canToggleSelection ? ' robot-row--selectable' : ''}`}
              key={robot.robotId}
              onClick={(event) => {
                if (event.target instanceof Element && event.target.closest('.robot-row__select, .robot-row__actions')) return;
                if (canToggleSelection) toggleSelected(robot.robotId);
              }}
            >
              <label className="robot-row__select">
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={!canToggleSelection}
                  onChange={() => toggleSelected(robot.robotId)}
                  aria-label={`${robot.displayName}を接続対象に選択`}
                />
                <span className={`robot-row__online${robot.online ? ' is-online' : ''}`} aria-hidden="true" />
              </label>
              <div className="robot-row__body">
                <div className="robot-row__title">
                  <strong>{robot.displayName}</strong>
                  <span className={`state-pill state-pill--${connectionState}`}>
                    {session ? STATE_LABELS[session.state] : preparationFailure ? STATE_LABELS[preparationFailure.state] : availabilityLabel}
                  </span>
                </div>
                <div className="robot-row__meta">
                  <code>{OFFLINE_MODE ? `ADB: ${robot.robotId}` : robot.robotId}</code>
                  {robot.online && (
                    <RobotBatteryIndicator level={robot.batteryLevel} charging={robot.isCharging} />
                  )}
                </div>
                {latestError && <p>{latestError}</p>}
              </div>
              <div className="robot-row__actions">
                <button
                  className={`camera-button${cameraPreview.state.robotId === robot.robotId && cameraPreview.state.status !== 'off' ? ' is-selected' : ''}`}
                  disabled={!isConnected || !canRepresentCurrentRun || cameraBusy}
                  onClick={() => void cameraPreview.toggleCamera(robot.robotId)}
                  title={isConnected ? 'カメラ映像とマイク音声を表示・終了' : '接続後にカメラを利用できます'}
                  aria-label={`${robot.displayName}のカメラ${cameraPreview.state.robotId === robot.robotId && cameraPreview.state.status !== 'off' ? 'を終了' : 'を表示'}`}
                  aria-pressed={cameraPreview.state.robotId === robot.robotId && cameraPreview.state.status !== 'off'}
                >
                  {cameraPreview.state.robotId === robot.robotId && cameraBusy
                    ? <LoaderCircle className="animate-spin" size={15} />
                    : <Camera size={15} />}
                </button>
                {isConnected && (
                  <button
                    className={`representative-button${isRepresentative ? ' is-selected' : ''}`}
                    disabled={pending || cameraBusy || !canRepresentCurrentRun}
                    onClick={() => remote.setRepresentative(robot.robotId)}
                    title="QRカメラの代表ロボットにする"
                    aria-label={`${robot.displayName}を代表ロボットにする`}
                  >
                    <Radio size={15} />
                  </button>
                )}
                {session && !isConnected && !isBusy && (
                  <button
                    className="robot-action-button"
                    disabled={locked}
                    onClick={() => void runAction(() => remote.reconnectRobot(robot.robotId))}
                    title="再接続"
                    aria-label={`${robot.displayName}へ再接続`}
                  >
                    <RefreshCw size={15} />
                  </button>
                )}
                {session && (
                  <button
                    className="robot-action-button robot-action-button--danger"
                    disabled={locked}
                    onClick={() => void runAction(() => remote.disconnectRobot(robot.robotId))}
                    title="切断"
                    aria-label={`${robot.displayName}を切断`}
                  >
                    <Unplug size={15} />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="robot-panel__footer">
        <button
          className="connect-button"
          disabled={locked || selected.size === 0}
          onClick={() => void connectSelected()}
        >
          {pending ? <LoaderCircle className="animate-spin" size={17} /> : <Link size={17} />}
          選択した{selected.size}台に接続
        </button>
        <p>{OFFLINE_MODE ? '緑の丸はADB接続可能な端末です。接続ボタンでロボットアプリ（USBオフラインモード）の応答を確認します。カメラボタンで代表ロボットの映像とマイク音声を表示できます（USB経由のため数fps・少し遅れます）。' : '緑の丸はオンラインです。カメラボタンで代表ロボットの映像を表示できます。'}</p>
      </div>
      </div>}

      {directoryView === 'users' && <section id="user-directory-content" className="user-directory" aria-label="接続可能なユーザー">
        <div className="user-directory__heading">
          <div>
            <p className="eyebrow">LIVE FLOW</p>
            <h3><Users size={16} /> 接続可能なユーザー</h3>
          </div>
          <div className="user-directory__heading-actions">
            <span className="user-directory__count">{users.length}</span>
            <button
              type="button"
              className="user-directory__refresh"
              aria-label="ユーザー一覧を更新"
              title="ユーザー一覧を更新"
              disabled={remote.directoryState !== 'connected'}
              onClick={() => remote.refreshUsers()}
            >
              <RefreshCw aria-hidden="true" size={15} />
            </button>
          </div>
        </div>
        {remote.userFlow.lastError && (
          <p className="user-directory__error" role="alert">{remote.userFlow.lastError}</p>
        )}
        <div className="user-directory__list">
          {users.length === 0 ? (
            <p className="user-directory__empty">共有中のユーザーはいません</p>
          ) : users.map((user) => {
            const selectedUser = remote.userFlow.selectedConnectionId === user.connectionId;
            const isCurrentUser = user.userId === currentUserId;
            return (
              <button
                type="button"
                className={`user-row${selectedUser ? ' is-selected' : ''}${isCurrentUser ? ' is-current-user' : ''}`}
                key={user.connectionId}
                aria-pressed={selectedUser}
                disabled={isCurrentUser}
                onClick={() => void remote.selectUserFlow(selectedUser ? null : user.connectionId)}
              >
                <span className="user-row__presence" aria-hidden="true" />
                <span className="user-row__body">
                  <strong>{user.displayName}</strong>
                  <span>{isCurrentUser
                    ? '自分'
                    : selectedUser
                    ? USER_CONNECTION_LABELS[remote.userFlow.connectionState]
                    : '接続可能'}</span>
                </span>
                {selectedUser && remote.userFlow.connectionState === 'connecting' && (
                  <LoaderCircle className="animate-spin" size={16} />
                )}
              </button>
            );
          })}
        </div>
      </section>}
    </aside>
  );
}
